import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { planKeys } from '../queries/keys';
import { planPaths } from '../queries/paths';
import { readVersion } from '../utils/version';

// TSS vite/03 — server state for planning.
//
// The rule this whole feature exists to hold up, from T01: a tier tied to v1
// KEEPS READING v1 after v2 is approved.
//
// Which is why nothing here ever resolves "the plan's current version" on the
// panel's side. A tier points at an exact version; approving a later one does
// not move it, and a screen that computed "the latest approved" would quietly
// undo the guarantee.

export const PLANS_PAGE_SIZE = 25;   // the backend default; hard cap is 100

export function usePlansList({ limit = PLANS_PAGE_SIZE, offset = 0, projectId } = {}) {
    const params = { limit, offset };
    if (projectId) params.project_id = projectId;

    return useQuery({
        queryKey: planKeys.list(params),
        queryFn: ({ signal }) => api.get(planPaths.list(), { params, signal }).then(r => r.data),
        placeholderData: keepPreviousData,
    });
}

// Identity and a summary of its versions. The list of versions has its own
// paginated route; this is the one that answers "what is this plan and where
// does it stand".
export function usePlan(id) {
    return useQuery({
        queryKey: planKeys.detail(id),
        queryFn: ({ signal }) => api.get(planPaths.detail(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

export function usePlanVersions(id, { limit = 50, offset = 0 } = {}) {
    const params = { limit, offset };
    return useQuery({
        queryKey: planKeys.versions(id, params),
        queryFn: ({ signal }) => api.get(planPaths.versions(id), { params, signal }).then(r => r.data),
        enabled: !!id,
        placeholderData: keepPreviousData,
    });
}

// The exact content of one version, its scope and its approval.
//
// READ THROUGH `readVersion`, and so is every write below that answers a
// version: the content, the scope and the approval arrive NEXT TO `version`,
// not inside it, and a screen that picked `data.version` alone opened every
// plan empty. A body that does not have the shape fails here, as a query
// error, instead of rendering as a blank plan.
//
// `number` is the version NUMBER — the 2 of "version 2" — and not the row's id.
// Mixing them up reads a different version without any error to show for it,
// which is why the two never share a name in this feature.
export function usePlanVersion(id, number) {
    return useQuery({
        queryKey: planKeys.version(id, number),
        queryFn: ({ signal }) => api
            .get(planPaths.version(id, number), { signal })
            .then(r => readVersion(r.data)),
        enabled: !!id && number != null,
    });
}

export function useCreatePlan() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(planPaths.list(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: planKeys.all }),
    });
}

// PATCH /admin/plans/{id} changes ONLY the name. The content is not here: it
// lives in the version, because the content of a plan is always the content of
// one of its versions.
export function useRenamePlan(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(planPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: planKeys.detail(id) });
            qc.invalidateQueries({ queryKey: planKeys.all });
        },
    });
}

// Saving a draft. `revision` is required and is the one that came with the row
// being edited — the same discipline as the tier and the task: whoever arrives
// second sends a revision that is no longer the row's and gets a 409 with the
// real one, instead of overwriting the other person's work.
//
// SAVING DOES NOT RAISE THE VERSION NUMBER. It writes another snapshot and
// moves the pointer, so ten saves leave ten snapshots and a single v1 — which
// is what makes it possible to reconstruct how the approved text was reached.
export function useSaveDraft(id, number) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api
            .patch(planPaths.version(id, number), payload)
            .then(r => readVersion(r.data)),
        // The answer IS the saved version, so it goes straight into the cache:
        // the screen shows what was saved without a second request racing it.
        onSuccess: (saved) => {
            qc.setQueryData(planKeys.version(id, number), saved);
            invalidatePlan(qc, id, number);
        },
    });
}

// The next draft, from an approved version.
//
// `source_version_id` IS REQUIRED, and it is the row's id — not its number.
// The route copies the content and the scope of the approved version it names
// into a NEW snapshot, and the approved one is left untouched. Sending nothing
// answers «Falta la versión aprobada de la que sale esta».
//
// One open draft or candidate per plan, held by a PARTIAL UNIQUE INDEX in the
// database and not by a check in JavaScript — two simultaneous requests both
// pass an `if`.
export function useCreateVersion(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (sourceVersionId) => api
            .post(planPaths.versions(id), { source_version_id: Number(sourceVersionId) })
            .then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: planKeys.detail(id) });
            qc.invalidateQueries({ queryKey: planKeys.all });
        },
    });
}

// The three state moves, each its own verb.
//
// NOTHING HERE IS OPTIMISTIC, and approving is the reason. Three of the gates
// that can refuse it live in the manager and nowhere the browser can evaluate:
// a human session (a token is refused even holding the permission), the
// project's scope, and being `lead` of one of its teams. Painting a version as
// approved and rolling it back on the 403 would show a signature that never
// happened, on the screen whose entire job is to not do that.
// LAS TRES MANDAN `revision`, Y NO ES OPCIONAL.
//
// It is the revision that was READ, and the contract says why in one sentence:
// «es lo único que permite darte cuenta de que alguien más guardó mientras
// escribías». Presenting or approving a version that changed underneath is
// exactly what it stops — the answer is a 409 with the revision there is now,
// instead of a signature over a text nobody read.
export function useSubmitVersion(id, number) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (revision) => api
            .post(planPaths.submit(id, number), { revision: Number(revision) })
            .then(r => readVersion(r.data)),
        onSuccess: () => invalidatePlan(qc, id, number),
    });
}

export function useRequestChanges(id, number) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ revision, reason }) => api
            .post(planPaths.requestChanges(id, number), { revision: Number(revision), reason })
            .then(r => readVersion(r.data)),
        onSuccess: () => invalidatePlan(qc, id, number),
    });
}

export function useApproveVersion(id, number) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (revision) => api
            .post(planPaths.approve(id, number), { revision: Number(revision) })
            .then(r => readVersion(r.data)),
        onSuccess: () => {
            invalidatePlan(qc, id, number);
            // An approval writes an event, and the pulse is showing events.
            qc.invalidateQueries({ queryKey: ['events'] });
        },
    });
}

// One place, because forgetting one of the three is how a screen ends up
// showing a version as a draft after it was approved.
function invalidatePlan(qc, id, number) {
    qc.invalidateQueries({ queryKey: planKeys.version(id, number) });
    qc.invalidateQueries({ queryKey: planKeys.detail(id) });
    qc.invalidateQueries({ queryKey: planKeys.all });
}
